<script>
/**
 * @component TreatmentList
 * @description Lista de protocolos de tratamiento con búsqueda, paginación y acciones CRUD.
 */
import { mapGetters } from "vuex";
import debounce from "debounce";

import pagination from "@/mixins/pagination";

import helperService from "@/services/helper";
import segmentService from "@/services/segment";
import treatmentService from "@/services/treatment";

import Spinner from "@/components/ui/Spinner";
import TrackingNav from "@/components/ui/TrackingNav.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "TreatmentList",

  mixins: [pagination],

  components: {
    Spinner,
    TrackingNav,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      treatments: [],
    };
  },

  computed: {
    ...mapGetters(["isPatient"]),
  },

  created() {
    this.getTreatments();

    this.$bus.$on("treatment-update", (treatment) => {
      const index = this.treatments.findIndex((p) => p._id === treatment._id);
      if (index >= 0) {
        this.$set(this.treatments, index, treatment);
      } else {
        this.treatments.unshift(treatment);
      }
    });
  },

  methods: {
    getTreatments(query = {}) {
      this.isLoading = true;
      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };
      treatmentService
        .get(defaultQuery)
        .then((res) => {
          this.treatments = res.docs || [];
          this.pagination.total = res.total || 0;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      this.getTreatments(
        helperService.buildQuery({
          offset: 0,
          searchTreatment: this.searchInput,
          queryProperties: ["name"],
        })
      );
    }, DEBOUNCE_MS_TIMEOUT),

    editTreatment(id) {
      this.$router.push({ name: "update-treatment", params: { id } });
    },

    deleteTreatment(treatment) {
      this.$confirm(
        `¿Desea eliminar el protocolo: ${treatment.name}?`,
        "Eliminar",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          segmentService.track({ name: "Treatment Deleted" });
          return treatmentService.delete(treatment._id);
        })
        .then(() => this.getTreatments())
        .catch(() => {});
    },

    goToTreatmentTasks(id) {
      helperService.callIfNoSelectedText(() => {
        this.$router.push({
          name: "treatment-tasks",
          params: { id },
        });
      });
    },

    onPageChange() {
      this.getTreatments();
    },
  },
};
</script>

<template lang="pug">
section.treatment-page
  tracking-nav(active="protocolos")

  //- Header bar
  .treatment-header
    .treatment-header__left
      .search-wrapper
        i.el-icon-search.search-icon
        input.search-input(
          v-model="searchInput"
          @input="debounceInput"
          placeholder="Buscar protocolo..."
        )
    .treatment-header__right
      router-link.btn-new(
        v-if="!isPatient"
        :to="{ name: 'create-treatment' }"
      )
        i.el-icon-plus
        span Nuevo protocolo

  //- Loading
  .treatment-loading(v-if="isLoading")
    spinner

  //- Empty
  .treatment-empty(v-if="!isLoading && !treatments.length")
    .treatment-empty__icon
      i.el-icon-document
    p No hay protocolos

  //- Table
  .treatment-table(v-if="!isLoading && treatments.length")
    .treatment-table__header
      span Nombre
      span Descripción
      span Acciones
    .treatment-table__row(
      v-for="treatment in treatments"
      :key="treatment._id"
      @click="goToTreatmentTasks(treatment._id)"
    )
      span.treatment-table__name {{ treatment.name }}
      span.treatment-table__desc {{ treatment.description }}
      .treatment-table__actions(@click.stop)
        button.icon-btn.icon-btn--default(
          @click="goToTreatmentTasks(treatment._id)"
          title="Ver tareas"
        )
          i.el-icon-s-operation
        button.icon-btn.icon-btn--default(
          v-if="!isPatient"
          @click="editTreatment(treatment._id)"
          title="Editar"
        )
          i.el-icon-edit
        button.icon-btn.icon-btn--danger(
          v-if="!isPatient"
          @click="deleteTreatment(treatment)"
          title="Eliminar"
        )
          i.el-icon-delete

  //- Pagination
  pagination(
    :isLoading="isLoading"
    :limit="pagination.limit"
    :total="pagination.total"
    @pagination="setPagination"
  )
</template>

<style lang="scss" scoped>
/* ── Header ── */
.treatment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.search-input {
  width: 280px;
  padding: 9px 14px 9px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #002d5e;
  color: #fff;
  border-radius: 10px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    background: #003d7a;
    color: #fff;
  }
}

/* ── Loading & Empty ── */
.treatment-loading {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.treatment-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 12px;
}

.treatment-empty__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 50%;

  i {
    font-size: 24px;
    color: #94a3b8;
  }
}

.treatment-empty p {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

/* ── Table ── */
.treatment-table {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  overflow: hidden;
}

.treatment-table__header {
  display: grid;
  grid-template-columns: 1fr 2fr 100px;
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  span {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.treatment-table__row {
  display: grid;
  grid-template-columns: 1fr 2fr 100px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0fafa;
  }
}

.treatment-table__name {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
}

.treatment-table__desc {
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.treatment-table__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 14px;
    color: #64748b;
  }
}

.icon-btn--default {
  &:hover {
    border-color: #002d5e;

    i {
      color: #002d5e;
    }
  }
}

.icon-btn--danger {
  &:hover {
    border-color: #dc2626;
    background: #fef2f2;

    i {
      color: #dc2626;
    }
  }
}
</style>
